import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSales } from "../../query/hooks";
import {
  ClockIcon,
  CurrencyDollarIcon,
  CubeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";
import PaymentReceipt from "../Common/PaymentReceipt";

const SaleHistory = () => {
  const { user, loading: authLoading } = useAuth();
  const salesQuery = useSales(!!user && !authLoading);
  const sales = Array.isArray(salesQuery.data) ? salesQuery.data : [];
  const loading = authLoading || (!!user && salesQuery.isLoading);
  const [message, setMessage] = useState("");
  const [receiptSale, setReceiptSale] = useState(null);

  useEffect(() => {
    if (salesQuery.isError) setMessage("Error loading sale history");
  }, [salesQuery.isError]);

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sale History</h1>
        <p className="text-gray-600">
          View all your completed material sales and payment status.
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            {message}
          </div>
        </div>
      )}

      {/* Sales List */}
      {sales?.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CurrencyDollarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No sales yet
          </h2>
          <p className="text-gray-600">
            Start by browsing materials, adding them to your bin, and completing
            a sale.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Your Sales ({sales?.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {sales.map((sale) => (
              <div key={sale._id} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Sale #{sale._id?.slice(-6) || "N/A"}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {new Date(sale?.createdAt).toLocaleDateString()} at{" "}
                      {new Date(sale?.createdAt).toLocaleTimeString()}
                    </p>
                  </div>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(
                      sale?.status
                    )}`}
                  >
                    {sale?.status}
                  </span>
                </div>

                {/* Materials List */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Materials Sold:
                  </h4>
                  <div className="space-y-2">
                    {sale?.materials?.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center">
                          <CubeIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-900">{item?.material?.name}</span>
                        </div>
                        <div className="text-gray-600">
                          {item.quantity}kg × ₦
                          {item?.material?.pricePerKg?.toFixed(2) || "0.00"} =
                          ₦
                          {
                            (item.price || 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-sm text-gray-600">
                    <p>
                      Total Quantity:{" "}
                      {sale?.totalQuantity}
                      kg
                    </p>
                    <p>
                      Payment Account: {sale?.bankDetails.bankName} -{" "}
                      {sale?.bankDetails?.accountNumber} {sale?.bankDetails?.accountName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">
                      ₦{sale?.totalPrice?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    {sale?.status === "paid" && sale?.receipt && (
                      <button
                        type="button"
                        onClick={() => setReceiptSale(sale)}
                        className="mt-3 rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white"
                      >
                        View receipt
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Payment Information
        </h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
          <li>
            Processing: Payment is being processed and will be sent to your bank
            account
          </li>
          <li>
            Paid: Payment has been successfully transferred to your bank account
          </li>
          <li>
            Payments are typically processed within 24-48 hours after sale
            completion
          </li>
          <li>
            Contact support if you haven't received payment within 3 business
            days
          </li>
        </ul>
      </div>
      {receiptSale && (
        <PaymentReceipt
          receipt={receiptSale.receipt}
          amount={receiptSale.totalPrice}
          onClose={() => setReceiptSale(null)}
        />
      )}
    </div>
  );
};

export default SaleHistory;
